import { ReactElement } from "react";
import styles from "./SharePointSettingsPage.module.less";
import { IValueInputAttachable } from "client/Forms/attachable";
import {
	Checkbox,
	FormItem,
	RadioButtonGroup,
	RadioButtonGroupItem,
	Select,
	Tabs,
	ValidationError
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { SettingsPage } from "../SettingsPage";
import { DocumentActionPermissionsFormData, OfficeAppsFormData } from "./SharePointSettingsPage";
import {
	UserAndUserGroupCollection,
	UserAndUserGroupKind
} from "client/Features/Users/UserAndUserGroupCollection/UserAndUserGroupCollection";
import { UsersAndUserGroupsDataModel } from "client/Api/UserGroupsManagement/userGroupsManagement";
import {
	attachToUserAndUserGroupCollection
} from "client/Features/Users/UserAndUserGroupCollection/attachable";
import {
	ActiveTab
} from "client/Features/Users/UserAndUserGroupTabSelector/UserAndUserGroupTabSelector";
import { DocumentActionConditionalAccessSettingsSaveModel, OfficeDocumentEditor } from "client/Api/Documents/common";
import { IpAddresses } from "client/Features/ConditionalAccess/IpAddresses";
import { OfficeWebAppOpenTarget } from "client/Api/Documents/SharePoint/common";

export type OfficeAppsTabProps = {
	attachable: IValueInputAttachable<OfficeAppsFormData>;
	usersAndUserGroupsData: UsersAndUserGroupsDataModel;
	isOfficeWebAppsOpenTargetSettingVisible: boolean;
};

export const OfficeAppsTab = (props: OfficeAppsTabProps): ReactElement => {
	const {
		attachable,
		usersAndUserGroupsData,
		isOfficeWebAppsOpenTargetSettingVisible
	} = props;

	return (
		<div className={styles.tabContent}>
			{isOfficeWebAppsOpenTargetSettingVisible && (
				<FormItem label={localize("_OfficeOnlineAppsOpenTarget")}>
					<Select
						{...attachable.for("officeWebAppOpenTarget").attachToSelect()}
						options={webAppOpenTargetOptions}
						menuTitle={localize("_OfficeOnlineAppsOpenTarget")}
					/>
					<ValidationError {...attachable.for("officeWebAppOpenTarget").attachToValidationError()} />
				</FormItem>
			)}

			<FormItem>
				<Checkbox
					{...attachable.for("allowToOpenInLocalApp").attachToCheckbox()}
					text={localize("_AllowToOpenInLocalApp")}
					ellipseText={false}
				/>
				<ValidationError {...attachable.for("allowToOpenInLocalApp").attachToValidationError()} />
			</FormItem>

			{attachable.for("allowToOpenInLocalApp").value && (
				<>
					<FormItem
						label={localize("_DefaultPreferenceForOpeningFiles")}
						isForGroup
					>
						<RadioButtonGroup
							{...attachable.for("defaultOfficeDocumentEditor").attachToRadioButtonGroup()}
							options={defaultOfficeDocumentEditorOptions}
						/>
						<ValidationError {...attachable.for("defaultOfficeDocumentEditor").attachToValidationError()} />
					</FormItem>

					<SettingsPage.Section
						title={localize("_OfficeLocalApps")}
						height="stretch"
					>
						<Tabs<TabId> height="fill">
							<Tabs.Tab<TabId>
								{...attachable.under("openLocalPermissions").attachToTab()}
								id="who-has-access"
								title={localize("_WhoHasAccess")}
							>
								<PermissionsTab
									attachable={attachable.for("openLocalPermissions")}
									usersAndUserGroupsData={usersAndUserGroupsData}
								/>
							</Tabs.Tab>

							<Tabs.Tab<TabId>
								{...attachable.under("openLocalConditionalAccessSettings").attachToTab()}
								id="conditional-access"
								title={localize("_ConditionalAccess")}
							>
								<ConditionalAccessTab
									attachable={attachable.for("openLocalConditionalAccessSettings")}
								/>
							</Tabs.Tab>
						</Tabs>
					</SettingsPage.Section>
				</>
			)}
		</div>
	);
};
OfficeAppsTab.displayName = "OfficeAppsTab";

export type PermissionsTabProps = {
	attachable: IValueInputAttachable<DocumentActionPermissionsFormData>;
	usersAndUserGroupsData: UsersAndUserGroupsDataModel;
};

export const PermissionsTab = (props: PermissionsTabProps): ReactElement => {
	const {
		attachable,
		usersAndUserGroupsData
	} = props;

	return (
		<div className={styles.tabContent}>
			<FormItem>
				<Checkbox
					{...attachable.for("everyoneHasAccess").attachToCheckbox()}
					text={localize("_EveryoneHasAccess")}
					ellipseText={false}
				/>
				<ValidationError {...attachable.for("everyoneHasAccess").attachToValidationError()} />
			</FormItem>

			{!attachable.for("everyoneHasAccess").value && (
				<UserAndUserGroupCollection
					{...attachToUserAndUserGroupCollection(
						attachable,
						{
							kind: UserAndUserGroupKind.Edit,
							dialogTitleWhenAddingUsersAndUserGroups: localize("_AddGroupsOrUsers")
						}
					)}
					activeTab={ActiveTab.UserGroup}
					allUsersAndUserGroupsData={usersAndUserGroupsData}
				/>
			)}
		</div>
	);
};
PermissionsTab.displayName = "PermissionsTab";

export type ConditionalAccessTabProps = {
	attachable: IValueInputAttachable<DocumentActionConditionalAccessSettingsSaveModel>;
};

export const ConditionalAccessTab = (props: ConditionalAccessTabProps): ReactElement => {
	const {
		attachable
	} = props;

	return (
		<FormItem
			label={localize("_SupportedOn")}
			isForGroup
		>
			<IpAddresses
				attachable={attachable}
			/>
		</FormItem>
	);
};
ConditionalAccessTab.displayName = "ConditionalAccessTab";

const defaultOfficeDocumentEditorOptions: readonly RadioButtonGroupItem<OfficeDocumentEditor>[] = [
	{
		text: localize("_OfficeOnlineApps"),
		value: OfficeDocumentEditor.Online
	},
	{
		text: localize("_OfficeLocalApps"),
		value: OfficeDocumentEditor.Local
	}
];

const webAppOpenTargetOptions: readonly RadioButtonGroupItem<OfficeWebAppOpenTarget>[] = [
	{
		text: localize("_OpenInPopup"),
		value: OfficeWebAppOpenTarget.Popup
	},
	{
		text: localize("_OpenInWorkspaceFrame"),
		value: OfficeWebAppOpenTarget.NewWorkspaceTab,
	},
	{
		text: localize("_OpenInOffice365Frame"),
		value: OfficeWebAppOpenTarget.NewOffice365Tab,
	}
];

type TabId = "who-has-access" | "conditional-access";
